import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import { Button } from '@coolremote/shared';
import clsx from 'clsx';

const useStyles = makeStyles((theme: any) => ({
  root: {
    transition: 'all 0.3s ease',
    width: 100,
  },
  disabled: {
    cursor: 'not-allowed !important',
    pointerEvents: 'unset !important' as any,
  },
  loading: {
    cursor: 'wait !important',
  },
  success: {
    backgroundColor: '#388e3c !important',
    color: '#ffffff !important',
    borderColor: '#388e3c !important',
    '&:hover': {
      backgroundColor: '#2e7d32 !important',
    },
    '& svg': {
      color: '#ffffff !important',
    }
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    justifyContent: 'center',
  },
  spinning: {
    animation: '$spin 0.6s linear infinite',
  },
  '@keyframes spin': {
    from: { transform: 'rotate(0deg)' },
    to: { transform: 'rotate(360deg)' },
  }
}));

export interface FlowButtonProps {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void | Promise<any>;
  label?: string;
  loadingLabel?: string;
  successLabel?: string;
  disabled?: boolean;
  white?: boolean;
  width?: number;
  className?: string;
  [key: string]: any;
}

/**
 * A highly interactive, state-driven button that automatically manages asynchronous transitions:
 * `Neutral` -> `Loading` (on click) -> `Success` (after promise resolves) -> Revert to `Neutral` (after 1.5s).
 *
 * It seamlessly supports both standard synchronous actions and asynchronous promise-returning onClick handlers.
 *
 * @component
 * @example
 * // Basic usage with async action:
 * const handleSave = async () => {
 *   await saveToDatabase();
 * };
 * 
 * return (
 *   <FlowButton 
 *     label="SET" 
 *     onClick={handleSave} 
 *   />
 * );
 * 
 * @example
 * // Customized labels:
 * const handleUpdate = () => new Promise((resolve) => setTimeout(resolve, 2000));
 * 
 * return (
 *   <FlowButton
 *     label="Sync Now"
 *     loadingLabel="Syncing..."
 *     successLabel="Synced!"
 *     onClick={handleUpdate}
 *   />
 * );
 */
const FlowButton: React.FC<FlowButtonProps> = ({
  onClick,
  label = 'SET',
  loadingLabel = 'Updating...',
  successLabel = 'APPLIED',
  disabled = false,
  white = true,
  width = 75,
  className,
  ...otherProps
}) => {
  const classes = useStyles();
  const [state, setState] = useState<'neutral' | 'loading' | 'success'>('neutral');

  const handleProgressClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || state !== 'neutral') {
      return;
    }

    if (onClick) {
      const result = onClick(e);
      if (result && typeof result.then === 'function') {
        setState('loading');
        try {
          await result;
          setState('success');
        } catch (error) {
          setState('neutral');
        }
      }
    }
  };

  useEffect(() => {
    if (state === 'success') {
      const timer = setTimeout(() => {
        setState('neutral');
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [state]);

  const renderContent = () => {
    if (state === 'loading') {
      return (
        <span className={classes.iconContainer}>
          <AutorenewIcon className={clsx(classes.spinning)} style={{ fontSize: '18px' }} />
          <span>{loadingLabel}</span>
        </span>
      );
    }

    if (state === 'success') {
      return (
        <span className={classes.iconContainer}>
          <CheckCircleIcon style={{ fontSize: '18px' }} />
          <span>{successLabel}</span>
        </span>
      );
    }

    return label;
  };

  return (
    <Button
      white={white}
      disabled={disabled || state === 'loading'}
      onClick={handleProgressClick}
      width={state === 'neutral' ? width : undefined}
      className={clsx(
        classes.root,
        disabled && classes.disabled,
        state === 'loading' && classes.loading,
        state === 'success' && classes.success,
        className
      )}
      {...otherProps}
    >
      {renderContent()}
    </Button>
  );
};

export default FlowButton;
